import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { API_CONFIG, getApiUrl } from "../config/api";
import { actingLocationHeaders } from "./actingLocation";
import {
  isEmptyObject,
  recordApiEmptyResponseWarning,
} from "./apiEmptyResponseWarning";
import { FeatureAccessResponse } from "../types/featureAccess";

/**
 * Routing calculation utility for post-login routing decisions.
 *
 * ROUTING LOGIC (using /access endpoint):
 * 1. Fetch /access first (contains onboarding state + user level)
 * 2. Handle 402 errors (trial expired / payment required)
 * 3. If non-Owner → /admin/calendar (skip onboarding)
 * 4. If Owner + initial onboarding incomplete → /account-creation
 * 5. If Owner + initial complete + progressive incomplete → /onboarding
 * 6. If Owner + all complete → /admin/calendar
 *
 * Fallback: If /access fails, check if salon exists
 */

export interface RoutingResult {
  destination: string;
  reason: string;
  paymentRequired?: boolean;
}

interface Salon {
  id: string;
  [key: string]: unknown;
}

/**
 * Make an authenticated fetch request using the current Amplify session
 */
async function fetchWithAuth(url: string): Promise<unknown> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error("No auth token available");
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...actingLocationHeaders(),
    },
  });

  if (!response.ok) {
    // Parse error body for 402/403 handling
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = {};
    }
    const error = new Error(
      `HTTP ${response.status}: ${response.statusText}`,
    ) as Error & { status: number; body: unknown };
    error.status = response.status;
    error.body = errorBody;
    throw error;
  }

  return response.json();
}

/**
 * Calculate the routing destination after login/password change.
 * Uses /access endpoint as the primary data source (replaces get_onboarding).
 *
 * @returns Promise<RoutingResult> - The destination path and reason
 */
export async function calculateRoutingDestination(): Promise<RoutingResult> {
  // Get the current user's ID (for fallback logic)
  try {
    await getCurrentUser();
  } catch (error) {
    console.error("Error getting current user:", error);
    return {
      destination: "/login",
      reason: "Could not get current user",
    };
  }

  // Step 1: Fetch /access (primary source - contains onboarding + user level)
  let accessData: FeatureAccessResponse | null = null;
  try {
    const accessUrl = getApiUrl(API_CONFIG.ENDPOINTS.ACCESS);
    accessData = (await fetchWithAuth(accessUrl)) as FeatureAccessResponse;
  } catch (error: unknown) {
    const typedError = error as {
      status?: number;
      body?: { message?: string };
    };

    // Handle 402 - trial expired or payment required
    if (typedError.status === 402) {
      return {
        destination: "/admin/settings/subscription",
        reason: typedError.body?.message || "Trial expired or payment required",
        paymentRequired: true,
      };
    }

    // For other errors, fall through to fallback logic
    console.error("Error fetching access:", error);
  }

  // If we have access data, use it for routing
  if (accessData) {
    const { onboarding, user_level } = accessData;

    // Non-owners (Manager/Employee/Assistant) skip onboarding
    if (
      user_level === "Manager" ||
      user_level === "Employee" ||
      user_level === "Assistant"
    ) {
      return {
        destination: "/admin/calendar",
        reason: `User is ${user_level} - skip onboarding`,
      };
    }

    // Check initial onboarding (Owner or missing user_level — treat as Owner for routing)
    if (
      onboarding?.initial_onboarding === "null" ||
      onboarding?.initial_onboarding === "inprogress"
    ) {
      return {
        destination: "/account-creation",
        reason: "Owner - initial onboarding incomplete",
      };
    }

    // Check progressive onboarding (null or inprogress means incomplete)
    const progressiveComplete =
      onboarding?.progressive_employees === "complete" &&
      onboarding?.progressive_services === "complete" &&
      onboarding?.progressive_payments === "complete";

    if (!progressiveComplete) {
      return {
        destination: "/onboarding",
        reason: "Owner - progressive onboarding incomplete",
      };
    }

    // All complete
    return {
      destination: "/admin/calendar",
      reason: "All onboarding complete",
    };
  }

  // Fallback: No access data - check if salon exists
  // (This handles the case where user has no staff record yet - new signup)
  try {
    const salonsUrl = getApiUrl(API_CONFIG.ENDPOINTS.SALONS);
    let salons = (await fetchWithAuth(salonsUrl)) as
      | Salon[]
      | Record<string, never>;

    // Backend returns 200 with {} when user has no salon
    if (isEmptyObject(salons)) {
      recordApiEmptyResponseWarning(
        "GET /business-locations",
        "Empty object (user has no salon yet)",
      );
      salons = [];
    }

    if (!salons || !Array.isArray(salons) || salons.length === 0) {
      return {
        destination: "/account-creation",
        reason: "No salon found - new Owner",
      };
    }

    // Has salon but no access data - likely needs onboarding
    return {
      destination: "/account-creation",
      reason: "Has salon but no access data - assuming needs onboarding",
    };
  } catch {
    // No salon access - new user
    return {
      destination: "/account-creation",
      reason: "Unable to fetch salon - defaulting to account creation",
    };
  }
}
